import { useMemo, useState } from 'react';
import { FileText, Plus } from 'lucide-react';
import { Button, Card, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Segmented } from '../../../shared/ui/Segmented.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { usePendingBackorderCount } from '../../backorder/model/backorder.queries.js';
import { BackordersView } from '../../backorder/ui/BackordersView.js';
import { useOrgUnits } from '../../org/model/org.queries.js';
import { useWarehouses } from '../../warehouse/model/warehouse.queries.js';
import type { RequisitionQuery } from '../api/requisition.api.js';
import type { BackorderTab, RequisitionsView } from '../model/requisitions-search.js';
import { useRequesterNames, useRequisitions } from '../model/requisition.queries.js';
import { requisitionErrorText } from '../model/requisition-error.js';
import {
  EMPTY_REQUISITION_FILTER,
  RequisitionFilters,
  type RequisitionFilterValue,
} from './RequisitionFilters.js';
import { RequisitionListCard } from './RequisitionListCard.js';

/** Filtr → so'rov. Bo'sh qiymat so'rovga UMUMAN qo'shilmaydi. */
const queryOf = (filter: RequisitionFilterValue): RequisitionQuery => ({
  ...(filter.status === '' ? {} : { status: filter.status }),
  ...(filter.warehouseId === '' ? {} : { warehouseId: filter.warehouseId }),
  ...(filter.mine ? { mine: true } : {}),
  limit: 50,
});

export interface RequisitionsPageProps {
  onOpen?: (requisitionId: string) => void;
  /** Yangi talabnoma ekraniga o'tish — sahifa marshrutlashni bilmaydi. */
  onCreate?: () => void;
  /**
   * Manzildagi holat. Sahifa routerni bilmaydi: qiymat va o'zgarish
   * chaqiruvi marshrut qatlamidan keladi. Berilmasa, holat sahifaning
   * o'zida yashaydi (sinovlar shunday ishlatadi).
   */
  view?: RequisitionsView;
  onViewChange?: (view: RequisitionsView) => void;
  backorderTab?: BackorderTab;
  onBackorderTabChange?: (tab: BackorderTab) => void;
}

/**
 * Talabnomalar ro'yxati — hamma ko'radigan umumiy ekran.
 *
 * Doirani BACKEND qo'yadi (`requisition.read` granti), sahifa esa faqat
 * filtrni beradi: "faqat meniki" ham shu yerda oddiy bayroq, chunki uni
 * server `?mine=true` bilan o'zi hal qiladi.
 */
export const RequisitionsPage = ({
  onOpen,
  onCreate,
  view: viewProp,
  onViewChange,
  backorderTab,
  onBackorderTabChange,
}: RequisitionsPageProps) => {
  const { canAnywhere } = usePermission();
  const [filter, setFilter] = useState<RequisitionFilterValue>(EMPTY_REQUISITION_FILTER);

  /*
   * BILDIRGILAR shu sahifaning ICHIDA — yon menyuda alohida bo'lim emas.
   *
   * Bildirgi talabnomaning oldingi bosqichi va oxirida talabnomaga
   * aylanadi. Alohida bo'limda turganda odam «so'ragan narsam qayerda»
   * deganda ikki bo'lim orasida yurardi.
   *
   * Almashtirgich faqat bildirgi ruxsati BORLARGA chiziladi: yo'q odamga
   * u bosilsa bo'sh ro'yxat ochadigan ma'nosiz tugma bo'lardi.
   */
  const canSeeBackorders =
    canAnywhere('backorder.create') ||
    canAnywhere('backorder.accept') ||
    canAnywhere('backorder.oversee');
  const [localView, setLocalView] = useState<RequisitionsView>('requisitions');
  const view = viewProp ?? localView;
  const setView = (next: RequisitionsView): void => {
    setLocalView(next);
    onViewChange?.(next);
  };
  const showingBackorders = canSeeBackorders && view === 'backorders';

  /* Skladchiga kelgan, lekin hali biriktirilmagan bildirgilar SONI —
     almashtirgich yonida. U ish talab qiladi va boshqa ko'rinishda turib
     ham ko'rinib turishi kerak. */
  const pendingCount = usePendingBackorderCount(canAnywhere('backorder.accept'));

  const list = useRequisitions(queryOf(filter));
  const warehouses = useWarehouses({ limit: 200 });
  /* Korxona nomlari ham SHU YERDAN: kartochka `features/org` ni bilmaydi,
     uni ilova qatlami bog'laydi — ombor nomi bilan ayni naqsh. */
  const { shortNameOf } = useOrgUnits();

  const warehouseNames = useMemo(
    () => new Map((warehouses.data?.items ?? []).map((w) => [w.id, w.name])),
    [warehouses.data],
  );

  const items = list.data?.items ?? [];
  /* Buyurtmachi ismi — ro'yxat javobida yo'q, hujjatdan olinadi. */
  const requesterNames = useRequesterNames(items);
  const canCreate = canAnywhere('requisition.create');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('requisition.title')}
        description={t('requisition.subtitle')}
        filtersLabel={t('requisition.filtersLabel')}
        /* Talabnoma filtrlari bildirgiga TEGISHLI EMAS — ko'rinishda
           ishlamaydigan filtr «nega hech narsa o'zgarmayapti» degan
           savol tug'dirardi. */
        {...(showingBackorders
          ? {}
          : {
              filters: (
                <RequisitionFilters
                  value={filter}
                  onChange={setFilter}
                  warehouses={warehouses.data?.items ?? []}
                />
              ),
            })}
        actions={
          canCreate && onCreate !== undefined && !showingBackorders ? (
            <Button onClick={onCreate}>
              <Plus size={16} aria-hidden />
              {t('requisition.newRequisition')}
            </Button>
          ) : undefined
        }
      />

      {canSeeBackorders ? (
        <Segmented
          label={t('requisition.viewLabel')}
          value={view}
          onChange={setView}
          options={[
            { value: 'requisitions', label: t('requisition.viewRequisitions') },
            {
              value: 'backorders',
              label: t('requisition.viewBackorders'),
              badge: pendingCount.data ?? 0,
              badgeLabel: t('backorder.pendingBadge', { count: pendingCount.data ?? 0 }),
            },
          ]}
          className="self-start"
        />
      ) : null}

      {showingBackorders ? (
        <BackordersView
          {...(backorderTab === undefined ? {} : { tab: backorderTab })}
          {...(onBackorderTabChange === undefined ? {} : { onTabChange: onBackorderTabChange })}
          {...(onOpen === undefined ? {} : { onOpenRequisition: onOpen })}
        />
      ) : list.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <Card>
          <EmptyState
            title={t('requisition.listError')}
            description={requisitionErrorText(list.error, '')}
            action={
              <Button variant="secondary" onClick={() => void list.refetch()}>
                {t('requisition.retry')}
              </Button>
            }
          />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText size={20} aria-hidden />}
            title={t('requisition.listEmpty')}
            description={t('requisition.listEmptyHint')}
          />
        </Card>
      ) : (
        <>
          {/* Kartochka — jadval emas. Ro'yxatning savoli "ustunlarni
              solishtirish" emas: odam MENING talabnomam qayerda va yana
              necha qadam qolganini bilmoqchi. Navbat ekrani (`InboxPage`)
              esa jadvalda qoladi: u ish ro'yxati va ko'z ustunlar bo'ylab
              yuguradi. */}
          <ul
            aria-label={t('requisition.listCaption')}
            className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
          >
            {items.map((row) => (
              <li key={row.id} className="min-w-0">
                <RequisitionListCard
                  requisition={row}
                  {...(warehouseNames.get(row.warehouseId) === undefined
                    ? {}
                    : { warehouseName: warehouseNames.get(row.warehouseId) as string })}
                  {...(shortNameOf(row.requesterOrgUnitId) === null
                    ? {}
                    : { requesterOrgUnitName: shortNameOf(row.requesterOrgUnitId) as string })}
                  {...(requesterNames.get(row.requesterUserId) === undefined
                    ? {}
                    : { requesterName: requesterNames.get(row.requesterUserId) as string })}
                  {...(onOpen === undefined ? {} : { onOpen: () => onOpen(row.id) })}
                />
              </li>
            ))}
          </ul>
          <p className={cn('tabular text-xs text-text-faint')}>
            {t('requisition.listTotal', { count: list.data?.total ?? items.length })}
          </p>
        </>
      )}
    </div>
  );
};
